import { useEffect, useState } from "react";
import api from "@/lib/api";
import { NotePencil, Check, X, Trash } from "@phosphor-icons/react";

const LEAD_STATUSES = [
  { value: "yeni",             label: "Yeni",                    dot: "bg-slate-400",  chip: "bg-slate-100 text-slate-700 border-slate-300" },
  { value: "aranacak",         label: "Aranacak",                dot: "bg-blue-500",   chip: "bg-blue-50 text-blue-700 border-blue-300" },
  { value: "arandi_cevap_yok", label: "Arandı, Cevap Vermedi",   dot: "bg-amber-500",  chip: "bg-amber-50 text-amber-700 border-amber-300" },
  { value: "ilgilenmiyor",     label: "İlgilenmiyor",            dot: "bg-red-500",    chip: "bg-red-50 text-red-700 border-red-300" },
  { value: "kayit_oldu",       label: "Kayıt Oldu",              dot: "bg-green-500",  chip: "bg-green-50 text-green-700 border-green-300" },
  { value: "islem_yok",        label: "İşlem Yok",               dot: "bg-stone-400",  chip: "bg-stone-100 text-stone-500 border-stone-300" },
];

const statusMeta = (v) => LEAD_STATUSES.find(s => s.value === v) || LEAD_STATUSES[0];

export function PriceLeads() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [noteEditId, setNoteEditId] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");

  const load = () => api.get("/admin/price-leads").then(r => setLeads(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const updateLead = async (id, patch) => {
    setLeads(ls => ls.map(l => l.id === id ? { ...l, ...patch } : l));
    try { await api.patch(`/admin/price-leads/${id}`, patch); }
    catch { load(); }
  };

  const startNote = (l) => { setNoteEditId(l.id); setNoteDraft(l.note || ""); };
  const saveNote = async (id) => { await updateLead(id, { note: noteDraft }); setNoteEditId(null); };
  const deleteLead = async (id) => {
    if (!window.confirm("Bu lead'i silmek istediğinize emin misiniz?")) return;
    await api.delete(`/admin/price-leads/${id}`);
    setLeads(ls => ls.filter(l => l.id !== id));
  };

  const counts = LEAD_STATUSES.reduce((acc, s) => ({ ...acc, [s.value]: leads.filter(l => (l.status || "yeni") === s.value).length }), { all: leads.length });
  const visible = filter === "all" ? leads : leads.filter(l => (l.status || "yeni") === filter);

  return (
    <div className="space-y-4" data-testid="price-leads-main">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-heading font-bold text-2xl">Fiyat Teklifleri</h3>
          <p className="text-sm text-stone-500 mt-0.5">Fiyat Danışmanı üzerinden gelen tüm lead'ler — durum işaretleyip not bırakabilirsiniz</p>
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setFilter("all")} data-testid="lead-filter-all"
          className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${filter === "all" ? "bg-brand-ink text-white border-brand-ink" : "bg-white border-stone-300 text-stone-600 hover:border-stone-400"}`}>
          Tümü ({counts.all})
        </button>
        {LEAD_STATUSES.map(s => (
          <button key={s.value} onClick={() => setFilter(s.value)} data-testid={`lead-filter-${s.value}`}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors flex items-center gap-1.5 ${filter === s.value ? `${s.chip} ring-2 ring-offset-1 ring-brand-primary/30` : "bg-white border-stone-300 text-stone-600 hover:border-stone-400"}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`}/> {s.label} ({counts[s.value]})
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {visible.length === 0 ? (
          <div className="nb-card p-12 text-center text-stone-500">Bu filtrede lead bulunamadı.</div>
        ) : visible.map(l => {
          const meta = statusMeta(l.status || "yeni");
          return (
            <div key={l.id} className="nb-card p-5" data-testid={`price-lead-${l.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-heading font-bold text-base">{l.full_name}</div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-brand-primarySubtle text-brand-primary uppercase tracking-wider">{l.course_class}</span>
                    {l.has_license && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-stone-100 text-stone-500">Mevcut: {l.existing_class || "—"}</span>}
                  </div>
                  <div className="text-sm text-stone-500 mt-1">{l.email} · {l.phone}</div>
                  <div className="text-xs text-stone-400 mt-1">{new Date(l.created_at).toLocaleString("tr-TR")}</div>

                  {/* Note */}
                  <div className="mt-3">
                    {noteEditId === l.id ? (
                      <div className="flex items-start gap-2">
                        <textarea autoFocus rows={2} value={noteDraft} onChange={e => setNoteDraft(e.target.value)}
                          className="nb-input !text-sm flex-1 resize-none" placeholder="Not ekleyin..." data-testid={`lead-note-input-${l.id}`}/>
                        <div className="flex flex-col gap-1">
                          <button onClick={() => saveNote(l.id)} className="p-1.5 rounded-lg bg-brand-success text-white" data-testid={`lead-note-save-${l.id}`}><Check weight="bold" size={13}/></button>
                          <button onClick={() => setNoteEditId(null)} className="p-1.5 rounded-lg bg-stone-200 text-stone-600"><X weight="bold" size={13}/></button>
                        </div>
                      </div>
                    ) : l.note ? (
                      <button onClick={() => startNote(l)} className="text-left text-sm text-stone-600 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 w-full hover:bg-stone-100 transition-colors" data-testid={`lead-note-view-${l.id}`}>
                        <span className="text-stone-400 text-xs font-semibold uppercase mr-1">Not:</span>{l.note}
                      </button>
                    ) : (
                      <button onClick={() => startNote(l)} className="flex items-center gap-1.5 text-xs font-semibold text-stone-400 hover:text-brand-primary transition-colors" data-testid={`lead-note-add-${l.id}`}>
                        <NotePencil weight="bold" size={14}/> Not ekle
                      </button>
                    )}
                  </div>
                </div>

                <div className="shrink-0 flex items-start gap-2">
                  <select value={l.status || "yeni"} onChange={e => updateLead(l.id, { status: e.target.value })}
                    className={`!py-2 !pl-3 !pr-8 rounded-full text-xs font-bold border cursor-pointer focus:outline-none ${meta.chip}`}
                    data-testid={`lead-status-select-${l.id}`}>
                    {LEAD_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                  <button onClick={() => deleteLead(l.id)} className="p-2 rounded-lg text-stone-400 hover:text-red-500 hover:bg-red-50 transition-colors" data-testid={`lead-delete-${l.id}`}>
                    <Trash weight="bold" size={15}/>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
